import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const blogPosts = [
  {
    id: 1,
    title: "How RestroAI Helped Us Handle Peak Hours Without Breaking a Sweat",
    author: "Rajesh Gupta",
    role: "Owner, Kashi Annapurna",
    city: "Varanasi",
    date: "January 2026",
    excerpt: "During festival season at the ghats, we used to lose track of orders and tables. RestroAI changed that—order management and billing are flawless even when the house is full. Our staff and customers are happier, and we finally have time to focus on the food.",
    icon: "🍽️",
  },
  {
    id: 2,
    title: "Revenue Up 28% in One Quarter: Our RestroAI Journey",
    author: "Amina Khan",
    role: "Owner, Royal Tunday",
    city: "Lucknow",
    date: "December 2025",
    excerpt: "From inventory to customer insights, everything is at our fingertips now. The AI-powered analytics showed us which dishes to promote and when. We cut waste, optimized our menu, and saw revenue jump by 28% in the first quarter. Best decision we ever made for our restaurant.",
    icon: "📈",
  },
  {
    id: 3,
    title: "From Spreadsheets to Smart Management in One Week",
    author: "Siddharth Deshpande",
    role: "Owner, FC Road Eats",
    city: "Pune",
    date: "November 2025",
    excerpt: "We were drowning in spreadsheets and missed orders. RestroAI was simple to set up and incredibly powerful. Real-time orders, table status, and staff management—all in one app. We switched and never looked back. The support team is excellent and always there when we need them.",
    icon: "📋",
  },
  {
    id: 4,
    title: "Multiple Outlets, One Dashboard: How We Scaled with RestroAI",
    author: "Farhan Ahmed",
    role: "Owner, Nawab's Biryani House",
    city: "Hyderabad",
    date: "October 2025",
    excerpt: "Running three outlets used to mean three different systems and a lot of confusion. RestroAI handles our high volume and all outlets with ease. The analytics on regional preferences helped us tailor our menu per location. Outstanding software for anyone thinking of scaling.",
    icon: "🏪",
  },
  {
    id: 5,
    title: "Keeping Up with Delhi's Pace—RestroAI Delivers",
    author: "Neha Sharma",
    role: "Owner, Chandni Chowk Delights",
    city: "Delhi",
    date: "September 2025",
    excerpt: "In Delhi, speed is everything. We needed something that could keep up. RestroAI delivers—orders, tables, and bills all in one place. Push notifications for new orders mean we never miss a beat. Customer satisfaction has never been higher, and so has our peace of mind.",
    icon: "⚡",
  },
];

const Blog = () => {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 overflow-hidden">
      {/* Background atmosphere */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
        <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[60vmax] h-[60vmax] bg-amber-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(251,191,36,0.12),transparent)]" />
      </div>

      {/* Header */}
      <header className="relative z-50 border-b border-stone-200/80 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-3 text-stone-700 hover:text-stone-900 transition-colors"
            >
              <img src="/logo-bright.svg" alt="RestroAI" className="h-9 w-auto" />
              <span className="text-sm font-medium text-stone-500">Blog</span>
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Home</Link>
              <Link to="/restaurants" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">Restaurants</Link>
              <Link
                to="/login"
                className="text-sm font-semibold text-amber-600 hover:text-amber-700 px-5 py-2.5 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 transition-all duration-300"
              >
                Login
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <motion.header
          className="mb-12"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-2">Stories</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">Blog</h1>
          <p className="text-stone-600">Stories and experiences from restaurant owners using RestroAI</p>
        </motion.header>

        <div className="space-y-6">
          {blogPosts.map((post, i) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-200/80 shadow-sm hover:shadow-md hover:border-amber-200/60 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <span className="text-4xl flex-shrink-0 w-14 h-14 flex items-center justify-center rounded-xl bg-amber-50 border border-amber-100" aria-hidden="true">{post.icon}</span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold text-stone-900 mb-2">{post.title}</h2>
                  <p className="text-stone-500 text-sm mb-1">
                    {post.author} · {post.role}, {post.city}
                  </p>
                  <p className="text-stone-400 text-xs mb-4">{post.date}</p>
                  <p className="text-stone-600 leading-relaxed">{post.excerpt}</p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <footer className="mt-16 pt-8 border-t border-stone-200 text-stone-500 text-sm text-center">
          <p>© {new Date().getFullYear()} RestroAI. All rights reserved.</p>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
            <Link to="/" className="hover:text-amber-600 transition-colors">Home</Link>
            <Link to="/contact-us" className="hover:text-amber-600 transition-colors">Contact</Link>
            <Link to="/privacy-policy" className="hover:text-amber-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-amber-600 transition-colors">Terms of Service</Link>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Blog;
